/*
 * Black November signup band.
 *
 * Posts straight to Klaviyo's client subscriptions endpoint so the band can
 * keep the design while the subscriber still lands in the right Klaviyo list.
 * The key in data-key is the public site ID — the same one already in every
 * page's Klaviyo script tag. A private key must never reach the browser.
 *
 * Content type and revision are the ones this endpoint documents, confirmed
 * by a real 202 from the storefront. They are deliberately NOT the ones in
 * assets/klaviyo-bis.js: that file calls a different endpoint
 * (/client/back-in-stock-subscriptions/) which tolerates plain JSON on an
 * older revision. Copying its headers here fails with
 * "'subscriptions' is not a valid field for the resource 'profile'", because
 * the consent block this sends did not exist on that revision.
 *
 * The submit button ships disabled and is enabled here on upgrade. Without
 * this script the form has no action, so a submit would navigate to the
 * current URL with the address in the query string — writing it into history,
 * the referrer and the server log, and dropping any filters already applied.
 * A disabled default button also suppresses implicit submission on Enter.
 */
if (!customElements.get('bn-signup')) {
  class BnSignup extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="email"]');
      this.button = this.querySelector('button[type="submit"]');
      this.status = this.querySelector('.bn-signup__status');
      this.consent = this.querySelector('.bn-signup__consent');
      const tpl = (name, fallbackAttr) => {
        const t = this.querySelector(`template[${name}]`);
        const html = t ? t.innerHTML.trim() : '';
        // A merchant can clear a richtext field; falling back keeps the form
        // from succeeding or failing in complete silence.
        return html || this.dataset[fallbackAttr] || '';
      };
      this.successHtml = tpl('data-success', 'fallbackSuccess');
      this.errorHtml = tpl('data-error', 'fallbackError');
      this.invalidHtml = tpl('data-invalid', 'fallbackInvalid') || this.errorHtml;
      this.busy = false;

      this.onSubmit = this.onSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      if (this.button) this.button.disabled = false;
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      const email = (this.input.value || '').trim();
      // Let the browser judge the address rather than inventing a pattern.
      if (!email || !this.input.checkValidity()) {
        this.input.focus();
        // Its own message: telling someone to check an address they never
        // typed is the kind of thing that makes a form feel broken.
        this.say(this.invalidHtml, 'error');
        return;
      }

      this.setBusy(true);
      try {
        const response = await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.key)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/vnd.api+json',
              revision: '2026-10-15',
            },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: this.dataset.source || 'Black November flash sale band',
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: {
                        email,
                        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                      },
                    },
                  },
                },
                relationships: {
                  list: { data: { type: 'list', id: this.dataset.list } },
                },
              },
            }),
          }
        );

        // 202 is the documented success, and what klaviyo-bis.js checks for.
        if (response.status !== 202) {
          const body = await response.text().catch(() => '');
          throw new Error(`Klaviyo responded ${response.status} ${body}`.trim());
        }

        this.form.hidden = true;
        if (this.consent) this.consent.hidden = true;
        // The flash sale landing page carries two of these forms plus a mobile
        // sticky bar, and one success has to settle all three. Deliberately no
        // address in the detail: nothing downstream needs it, and a bubbling
        // event is readable by anything else on the page.
        document.dispatchEvent(
          new CustomEvent('bn-signup:success', { detail: { source: this.dataset.source || '' } })
        );
        // Focus only once the text is actually in the element: hiding the form
        // drops focus to <body>, and focusing an empty <p> gives a screen
        // reader nothing to read.
        this.say(this.successHtml, 'success', () => {
          if (this.status) this.status.focus();
        });
      } catch (error) {
        console.error('[bn-signup]', error);
        // Disabling the button moved focus to <body>; put it back on the field
        // the shopper needs, rather than the top of the document.
        this.say(this.errorHtml, 'error', () => {
          if (this.input) this.input.focus();
        });
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.button) {
        this.button.disabled = busy;
        this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
    }

    say(html, kind, then) {
      if (!this.status) {
        if (then) then();
        return;
      }
      this.status.classList.toggle('is-error', kind === 'error');
      this.status.classList.toggle('is-success', kind === 'success');
      // Emptied first, then filled after a beat. A live region given the same
      // text twice is usually not announced again, so a second failed attempt
      // would be silent; one animation frame is short enough that assistive
      // tech can coalesce the two mutations, so this waits a little longer.
      this.status.textContent = '';
      setTimeout(() => {
        this.status.innerHTML = html;
        if (then) then();
      }, 120);
    }
  }

  customElements.define('bn-signup', BnSignup);
}
